/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useMemo } from 'react';
import { useShallowStableReference } from '@jira/platform__utils-stable-reference/src/index.tsx';
import type { AnyEntryPoint, ParamsOfEntryPoint } from '../../common/types.tsx';

import { DEFAULT_OPTIONS } from '../constants.tsx';
import type { Metric, Options } from '../types.tsx';
import type { EntryPointActions } from '../utils/types.tsx';
import { useEntryPointActions } from '../utils/use-entry-point-actions/index.tsx';
import {
	useEntryPointLoadManager,
	type EntryPointReferenceSubject,
} from '../utils/use-entry-point-load-manager/index.tsx';
import { useMetricLogger, type StopMetric } from '../utils/use-metric-logger/index.tsx';

export type UseEntryPoint<TEntryPoint extends AnyEntryPoint> = {
	entryPointActions: EntryPointActions;
	entryPointReferenceSubject: EntryPointReferenceSubject<TEntryPoint>;
	stopMetric: StopMetric;
};

/**
 * @deprecated Use Platform EntryPoints API instead instead. https://go/platform-entry-points
 * */
export const useEntryPoint = <TEntryPoint extends AnyEntryPoint>(
	entryPoint: TEntryPoint,
	entryPointParams: ParamsOfEntryPoint<TEntryPoint>,
	metric?: Metric,
	options: Options = DEFAULT_OPTIONS,
): UseEntryPoint<TEntryPoint> => {
	// TODO: break tests on instability (stableEntryPointParams!==entryPointParams)
	// prevents {} from from triggering updates
	const stableEntryPointParams = useShallowStableReference(entryPointParams);
	const { entryPointReferenceSubject, ...loadActions } = useEntryPointLoadManager<TEntryPoint>(
		entryPoint,
		stableEntryPointParams,
	);

	const { startMetric, stopMetric } = useMetricLogger(metric);
	const entryPointActions = useEntryPointActions(loadActions, options, startMetric);

	return useMemo(
		() => ({
			entryPointActions,
			entryPointReferenceSubject,
			stopMetric,
		}),
		[entryPointActions, entryPointReferenceSubject, stopMetric],
	);
};
